import PropTypes from 'prop-types';
import { Card, Heading, Inline, RadioCard, Stack, vars } from '@etg/wings';
import { Notification, variants } from '@eti/components';
import { useProperty, useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import { BrandedFare as BrandedFareType } from '@eti/types';
import { constants, price as priceUtil } from '@eti/utils';
import { Check, X } from '@phosphor-icons/react';
import { getTravelerPrices } from '../../../../utils/travelers';
import { getTripDisplayPrice, getIncludedBaggageText } from '../../../result/utils/resultUtils';
import { INCLUDED_PERSONAL_ITEM } from '../../../../constants/includedBaggageConstants';

const { PER_ADULT, PER_TRAVELER, TOTAL } = constants.priceDisplayTypes;

const cardStyles = css`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const featureSectionStyles = css`
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
`;

const notificationStyles = css`
  margin-top: 16px;
`;

const featureIconStyles = css`
  flex-shrink: 0;
`;

const featureIconProps = {
  'aria-hidden': true,
  className: featureIconStyles,
  size: 20,
  weight: 'light',
};

const Feature = ({ isIncluded, label }) => {
  const icon = isIncluded ? (
    <Check color={vars.colors.signal.positive.foreground} {...featureIconProps} />
  ) : (
    <X color="#262626" {...featureIconProps} />
  );
  return label ? (
    <Inline alignY="top" as="li" noWrap role="listitem" spacing="8">
      {icon}
      <p>{label}</p>
    </Inline>
  ) : null;
};

Feature.propTypes = {
  isIncluded: PropTypes.bool,
  label: PropTypes.string,
};

const resolvePrice = (brandedFareData, tripPriceDisplayType) => {
  const { travelers, travelerPrices } = brandedFareData;
  const groupedTravelerPrices = getTravelerPrices(travelers, travelerPrices);
  return getTripDisplayPrice(tripPriceDisplayType)(groupedTravelerPrices);
};

const getFormattedPrice = (brandedFareData, tripPriceDisplayType) =>
  priceUtil.formatPrice(resolvePrice(brandedFareData, tripPriceDisplayType));

const getCheckedBaggageFeature = (
  showNotIncludedProducts,
  checkedBaggageSummary,
  brandedFareData,
  t,
) => {
  let component = null;
  if (checkedBaggageSummary && checkedBaggageSummary.baggageStatus === 'INCLUDED_CHECKED_BAGGAGE') {
    const checkedInBaggageName = getIncludedBaggageText(
      checkedBaggageSummary.baggageDetails,
      'IbeClient.CheckInBaggageIncluded.Label',
      t,
    );
    component = (
      <Feature
        isIncluded
        key={`${brandedFareData.id}${checkedInBaggageName}`}
        label={checkedInBaggageName}
      />
    );
  } else if (
    showNotIncludedProducts &&
    checkedBaggageSummary &&
    (checkedBaggageSummary.baggageStatus === 'NO_CHECKED_BAGGAGE' ||
      checkedBaggageSummary.baggageStatus === 'NO_CHECKED_BAGGAGE_AVAILABLE')
  ) {
    const checkedInBaggageName = getIncludedBaggageText(
      { pieces: 1 },
      'BrandedFares.CheckInBaggageNotIncluded.Text',
      t,
    );
    component = (
      <Feature key={`${brandedFareData.id}${checkedInBaggageName}`} label={checkedInBaggageName} />
    );
  }
  return component;
};

const getCabinBaggageFeature = (
  showNotIncludedProducts,
  cabinBaggageTripSummary,
  brandedFareData,
  t,
) => {
  let component = null;
  if (
    cabinBaggageTripSummary &&
    cabinBaggageTripSummary.cabinBaggageStatus === 'INCLUDED_CABIN_BAGGAGE'
  ) {
    const cabinBaggageName = getIncludedBaggageText(
      cabinBaggageTripSummary.cabinBaggageDetails,
      'IbeClient.CabinBaggageIncluded.Label',
      t,
    );
    component = (
      <Feature
        isIncluded
        key={`${brandedFareData.id}${cabinBaggageName}`}
        label={cabinBaggageName}
      />
    );
  } else if (
    showNotIncludedProducts &&
    cabinBaggageTripSummary &&
    (cabinBaggageTripSummary.cabinBaggageStatus === 'NO_CABIN_BAGGAGE' ||
      cabinBaggageTripSummary.cabinBaggageStatus === 'NO_CABIN_BAGGAGE_AVAILABLE')
  ) {
    const cabinBaggageName = getIncludedBaggageText(
      { pieces: 1 },
      'BrandedFares.CabinBaggageNotIncluded.Text',
      t,
    );
    component = (
      <Feature key={`${brandedFareData.id}${cabinBaggageName}`} label={cabinBaggageName} />
    );
  }
  return component;
};

const getPersonalAndCabinBaggageInfo = (isPersonalItemIncluded, cabinBaggageTripSummary, t) => {
  let personalAndCabinText = '';
  let isIncluded = true;
  if (
    isPersonalItemIncluded &&
    cabinBaggageTripSummary?.cabinBaggageStatus === 'INCLUDED_CABIN_BAGGAGE'
  ) {
    personalAndCabinText = getIncludedBaggageText(
      cabinBaggageTripSummary?.cabinBaggageDetails,
      'BrandedFares.PersonalItemAndCabinBaggageIncluded.Text',
      t,
    );
  } else if (
    isPersonalItemIncluded &&
    (cabinBaggageTripSummary.cabinBaggageStatus === 'NO_CABIN_BAGGAGE' ||
      cabinBaggageTripSummary.cabinBaggageStatus === 'NO_CABIN_BAGGAGE_AVAILABLE')
  ) {
    personalAndCabinText = t('BrandedFares.PersonalItemIncludedButNoCabinBaggage.Text');
  } else if (
    !isPersonalItemIncluded &&
    cabinBaggageTripSummary?.cabinBaggageStatus === 'INCLUDED_CABIN_BAGGAGE'
  ) {
    personalAndCabinText = getIncludedBaggageText(
      cabinBaggageTripSummary?.cabinBaggageDetails,
      'BrandedFares.NoPersonalItemButCabinBaggageIncluded.Text',
      t,
    );
  } else if (
    !isPersonalItemIncluded &&
    (cabinBaggageTripSummary.cabinBaggageStatus === 'NO_CABIN_BAGGAGE' ||
      cabinBaggageTripSummary.cabinBaggageStatus === 'NO_CABIN_BAGGAGE_AVAILABLE')
  ) {
    personalAndCabinText = t('BrandedFares.NoPersonalItemAndNoCabinBaggageIncluded.Text');
    isIncluded = false;
  }
  return { isIncluded, personalAndCabinText };
};

const getPersonalItemAndCabinBaggageFeature = (
  showNotIncludedProducts,
  isPersonalItemIncluded,
  cabinBaggageTripSummary,
  brandedFareData,
  p,
  t,
) => {
  let component = null;
  const personalAndCabinBaggageShowType = String(
    p('BrandedFares.PersonalAndCabinBaggage.ShowType'),
  ).toUpperCase();
  const personalAndCabinBaggageDefaultShowType = 'SEPARATE';
  const personalAndCabinBaggageShowTypePair = 'PAIR';
  if (personalAndCabinBaggageShowType === personalAndCabinBaggageDefaultShowType) {
    const personalItemText = isPersonalItemIncluded
      ? t('BrandedFares.PersonalBaggageIncluded.Text')
      : t('BrandedFares.PersonalBaggageNotIncluded.Text');
    const cabinBaggageFeature = getCabinBaggageFeature(
      showNotIncludedProducts,
      cabinBaggageTripSummary,
      brandedFareData,
      t,
    );
    component = (
      <>
        <Feature
          isIncluded={Boolean(isPersonalItemIncluded)}
          key={`${brandedFareData.id}${personalItemText}`}
          label={personalItemText}
        />
        {cabinBaggageFeature}
      </>
    );
  } else if (personalAndCabinBaggageShowType === personalAndCabinBaggageShowTypePair) {
    const { isIncluded, personalAndCabinText } = getPersonalAndCabinBaggageInfo(
      isPersonalItemIncluded,
      cabinBaggageTripSummary,
      t,
    );
    component = (
      <Feature
        isIncluded={isIncluded}
        key={`${brandedFareData.id}${personalAndCabinText}`}
        label={personalAndCabinText}
      />
    );
  }
  return component;
};

const getBrandedFareFeature = (showNotIncludedProducts, brandedFareData, feature) => {
  let component = null;
  if (feature.availability === 'INCLUDED') {
    component = (
      <Feature isIncluded key={`${brandedFareData.id}${feature.name}`} label={feature.name} />
    );
  } else if (showNotIncludedProducts) {
    component = <Feature key={`${brandedFareData.id}${feature.name}`} label={feature.name} />;
  }
  return component;
};

const BrandedFare = ({ brandedFareData, shouldShowInformationBox, ticketingCarrierCodes = [] }) => {
  const { t } = useTranslation();
  const { p } = useProperty();

  const tripPriceText = {
    [PER_ADULT]: t('Result.PricePerAdult.Text'),
    [PER_TRAVELER]: t('Result.PricePerPerson.Text'),
    [TOTAL]: t('Result.TotalPrice.Text'),
  };
  const tripPriceDisplayType = p('BrandedFares.ShowPrice.Type') || PER_ADULT;
  const informationBoxText = t('BrandedFares.BasicOption.Information.Text');
  const checkedBaggageSummary = brandedFareData?.checkedBaggageSummary?.tripSummaries?.[0];
  const cabinBaggageTripSummary =
    brandedFareData?.cabinBaggageTripSummary?.cabinBaggageTravelerDetails?.[0];
  const personalItemTripSummary =
    brandedFareData?.personalItemTripSummary?.personalItemTravelerDetails[0];
  const isPersonalItemIncluded =
    personalItemTripSummary?.personalItemStatus === INCLUDED_PERSONAL_ITEM;
  const carriersNotIncludedProducts = p('BrandedFares.ShowNotIncluded.Carriers') || [];
  const shouldUseCabinBaggageTextUsingAtpco = p('BrandedFares.CabinBaggage.From.Atpco.Enabled');
  const showNotIncludedProducts = carriersNotIncludedProducts.some((item) =>
    ticketingCarrierCodes.includes(item),
  );

  const shouldShowPersonalItem = p('BrandedFares.ShowPersonalBaggage.Enabled');

  return (
    <RadioCard
      className={cardStyles}
      data-testid={`brandedFare-option-${brandedFareData.id}`}
      id={brandedFareData.id}
      label={
        <Heading as="span" level={3}>
          {brandedFareData.name}
        </Heading>
      }
      value={brandedFareData.selectionKey}
    >
      <Card.Section>
        <Heading data-testid={`brandedFare-${brandedFareData.id}-price`} level={4}>
          {getFormattedPrice(brandedFareData, tripPriceDisplayType)}
        </Heading>
        <p>{tripPriceText[tripPriceDisplayType]}</p>
      </Card.Section>
      <Card.Section className={featureSectionStyles}>
        <Stack as="ul" spacing="8">
          {!shouldUseCabinBaggageTextUsingAtpco &&
            shouldShowPersonalItem &&
            getPersonalItemAndCabinBaggageFeature(
              showNotIncludedProducts,
              isPersonalItemIncluded,
              cabinBaggageTripSummary,
              brandedFareData,
              p,
              t,
            )}

          {!shouldUseCabinBaggageTextUsingAtpco &&
            !shouldShowPersonalItem &&
            getCabinBaggageFeature(
              showNotIncludedProducts,
              cabinBaggageTripSummary,
              brandedFareData,
              t,
            )}

          {getCheckedBaggageFeature(
            showNotIncludedProducts,
            checkedBaggageSummary,
            brandedFareData,
            t,
          )}
          {brandedFareData.features.map((feature) =>
            getBrandedFareFeature(showNotIncludedProducts, brandedFareData, feature),
          )}
        </Stack>
        {shouldShowInformationBox && informationBoxText && (
          <Notification
            className={notificationStyles}
            dataTestId="brandedFares-firstOption-notification"
            variant={variants.INFORMATION}
          >
            {informationBoxText}
          </Notification>
        )}
      </Card.Section>
    </RadioCard>
  );
};

BrandedFare.propTypes = {
  brandedFareData: BrandedFareType,
  shouldShowInformationBox: PropTypes.bool,
  ticketingCarrierCodes: PropTypes.array,
};

export default BrandedFare;
